import { type GroupBase, type StylesConfig } from 'react-select';

import { focusVisible, focusVisibleTransitionValue } from '../helpers';
import { scrollbarStyling } from '../helpers';
import { type InputSize } from '../helpers/Input';

type FontToken<T extends string = string> = `var(--dds-font-${T})`;
type LetterSpacingToken<T extends string = string> =
  `var(--dds-font-${T}-letter-spacing)`;

interface StaticSelectTypography<T extends string = string> {
  font: FontToken<T>;
  letterSpacing: LetterSpacingToken<T>;
}

type SelectTypography = Record<InputSize, StaticSelectTypography>;

const optionTypography: SelectTypography = {
  medium: {
    font: 'var(--dds-font-body-short-medium)',
    letterSpacing: 'var(--dds-font-body-short-medium-letter-spacing)',
  },
  small: {
    font: 'var(--dds-font-body-short-small)',
    letterSpacing: 'var(--dds-font-body-short-small-letter-spacing)',
  },
  xsmall: {
    font: 'var(--dds-font-body-short-xsmall)',
    letterSpacing: 'var(--dds-font-body-short-xsmall-letter-spacing)',
  },
};

const controlTypography: SelectTypography = optionTypography;

const multiValueLabelTypography: SelectTypography = {
  medium: {
    font: 'var(--dds-font-body-short-small)',
    letterSpacing: 'var(--dds-font-body-short-small-letter-spacing)',
  },
  small: {
    font: 'var(--dds-font-body-short-small)',
    letterSpacing: 'var(--dds-font-body-short-small-letter-spacing)',
  },
  xsmall: {
    font: 'var(--dds-font-body-short-xsmall)',
    letterSpacing: 'var(--dds-font-body-short-xsmall-letter-spacing)',
  },
};

const groupHeadingTypography: StaticSelectTypography = {
  font: 'var(--dds-font-heading-xsmall)',
  letterSpacing: 'var(--dds-font-heading-xsmall-letter-spacing)',
};

const typography = {
  option: optionTypography,
  control: controlTypography,
  multiValueLabel: multiValueLabelTypography,
  groupHeading: groupHeadingTypography,
};

const clearButtonSpacing: Record<InputSize, string> = {
  xsmall: 'var(--dds-spacing-x0-125)',
  small: 'var(--dds-spacing-x0-25)',
  medium: 'var(--dds-spacing-x0-5)',
};

export const prefix = 'dds-select';

export const getCustomStyles = <TOption>(
  size: InputSize,
  hasError: boolean,
  isReadOnly?: boolean,
): Partial<StylesConfig<TOption, boolean, GroupBase<TOption>>> => ({
  control: (provided, state) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    cursor: 'pointer',
    borderRadius: 'var(--dds-border-radius-input)',
    border: '1px solid',
    borderColor: 'var(--dds-color-border-default)',
    backgroundColor: 'var(--dds-color-surface-default)',
    transition: `box-shadow var(--dds-motion-micro-state), border-color var(--dds-motion-micro-state), ${focusVisibleTransitionValue}`,
    ...typography.control[size],
    '&:hover': {
      ...(!isReadOnly && {
        borderColor: 'var(--dds-color-border-action-hover)',
        boxShadow: '0 0 0 1px var(--dds-color-border-action-hover)',
      }),
    },
    ...(hasError && {
      borderColor: 'var(--dds-color-border-danger)',
      boxShadow: '0 0 0 1px var(--dds-color-border-danger)',
      '&:hover': {
        borderColor: 'var(--dds-color-border-danger)',
        boxShadow: '0 0 0 1px var(--dds-color-border-danger)',
      },
    }),
    '&:focus-within': {
      ...focusVisible,
    },
    ...(state.selectProps.isDisabled && {
      borderColor: 'var(--dds-color-border-subtle)',
      backgroundColor: 'var(--dds-color-surface-field-disabled)',
      color: 'var(--dds-color-text-subtle)',
      pointerEvents: 'none',
    }),
    ...(isReadOnly && {
      borderColor: 'var(--dds-color-border-default)',
      backgroundColor: 'var(--dds-color-surface-field-disabled)',
      color: 'var(--dds-color-text-medium)',
      boxShadow: 'none',
      cursor: 'default',
    }),
  }),
  placeholder: provided => ({
    ...provided,
    color: 'var(--dds-color-text-subtle)',
    margin: 0,
  }),
  input: provided => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({}),
  dropdownIndicator: (provided, state) => ({
    display: 'inline-flex',
    '@media (prefers-reduced-motion: no-preference)': {
      transition:
        'color var(--dds-motion-micro-state), transform var(--dds-motion-chevron)',
    },
    padding: 0,
    color: 'var(--dds-color-icon-default)',
    ...(state.selectProps.isDisabled && {
      color: 'var(--dds-color-icon-subtle)',
    }),
    ...(isReadOnly && { color: 'var(--dds-color-icon-subtle)' }),
  }),

  valueContainer: (provided, state) => ({
    ...provided,
    ...(state.selectProps.isMulti && {
      gap: 'var(--dds-spacing-x0-25)',
    }),
    padding: 0,
    overflowY: 'visible',
  }),

  singleValue: () => ({
    gridArea: '1/1/2/3',
    overflow: 'hidden',
    boxSizing: 'border-box',
  }),
  multiValue: (provided, state) => ({
    boxSizing: 'border-box',
    minWidth: 0,
    display: 'flex',
    borderRadius: 'var(--dds-border-radius-surface)',
    backgroundColor: 'var(--dds-color-surface-medium)',
    ...((state.selectProps.isDisabled || isReadOnly) && {
      boxShadow: '0 0 0 1px var(--dds-color-border-subtle) inset',
    }),
    ...typography.multiValueLabel[size],
    color: 'var(--dds-color-text-default)',
  }),
  multiValueLabel: (provided, state) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    paddingInlineStart: 'var(--dds-spacing-x0-25)',
    paddingBlock: 'var(--dds-spacing-x0-125)',
    ...(state.selectProps.isDisabled && {
      color: 'var(--dds-color-text-subtle)',
    }),
    ...(isReadOnly && {
      color: 'var(--dds-color-text-medium)',
    }),
    ...((state.selectProps.isDisabled || isReadOnly) && {
      paddingInlineEnd: 'var(--dds-spacing-x0-25)',
    }),
  }),
  multiValueRemove: (provided, state) =>
    state.selectProps.isDisabled || isReadOnly
      ? {
          display: 'none',
        }
      : {
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          borderEndEndRadius: 'var(--dds-border-radius-surface)',
          borderStartEndRadius: 'var(--dds-border-radius-surface)',
          '@media (prefers-reduced-motion: no-preference)': {
            transition:
              'color var(--dds-motion-micro-state), background-color var(--dds-motion-micro-state), box-shadow var(--dds-motion-micro-state)',
          },
          '&:hover': {
            backgroundColor: 'var(--dds-color-surface-hover-default)',
          },
        },
  menu: provided => ({
    ...provided,
    boxSizing: 'border-box',
    width: '100%',
    boxShadow: 'var(--dds-shadow-medium)',
    zIndex: 100,
    transition: 'var(--dds-motion-surface)',
    border: '1px solid var(--dds-color-border-default)',
    backgroundColor: 'var(--dds-color-surface-default)',
    borderRadius: 'var(--dds-border-radius-surface)',
    marginBlock: 'var(--dds-spacing-x0-25)',
  }),
  menuPortal: provided => ({
    ...provided,
    zIndex: 99999,
  }),
  group: () => ({
    boxSizing: 'border-box',
  }),
  groupHeading: () => ({
    ...typography.groupHeading,
    color: 'var(--dds-color-text-default)',
    padding:
      'var(--dds-spacing-x0-75) var(--dds-spacing-x0-75) 0 var(--dds-spacing-x0-75)',
  }),
  menuList: () => ({
    maxHeight: '300px',
    overflowY: 'auto',
    position: 'relative',
    boxSizing: 'border-box',
    padding: 'var(--dds-spacing-x0-125)',
    ...scrollbarStyling,
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--dds-spacing-x0-25)',
    padding: 'var(--dds-spacing-x0-75)',
    borderRadius: 'var(--dds-border-radius-surface)',
    backgroundColor: 'var(--dds-color-surface-default)',
    ...typography.option[size],
    color: 'var(--dds-color-text-default)',
    '@media (prefers-reduced-motion: no-preference)': {
      transition:
        'color var(--dds-motion-micro-state), background-color var(--dds-motion-micro-state)',
    },
    ...(state.isSelected && {
      backgroundColor: 'var(--dds-color-surface-selected-default)',
    }),
    // egen stil siden react-select bruker focus-state og hover-styling samtidig; ikke nødvendig hvis de kan skilles.
    ...(state.isFocused && {
      outline: 'var(--dds-color-border-action-hover) 2px solid',
      outlineOffset: 'calc(var(--dds-spacing-x0-125) * -1)',
    }),
  }),
  noOptionsMessage: () => ({
    ...typography.option[size],
    padding: 'var(--dds-spacing-x0-5) var(--dds-spacing-x1)',
    color: 'var(--dds-color-text-medium)',
  }),
  clearIndicator: () =>
    isReadOnly
      ? { display: 'none' }
      : {
          display: 'inline-flex',
          color: 'var(--dds-color-icon-default)',
          borderRadius: 'var(--dds-border-radius-button)',
          marginBlock: `calc(-1 * ${clearButtonSpacing[size]})`,
          padding: clearButtonSpacing[size],

          '@media (prefers-reduced-motion: no-preference)': {
            transition: 'background-color var(--dds-motion-micro-state)',
          },
          '&:hover': {
            backgroundColor: 'var(--dds-color-surface-hover-default)',
          },
        },
  loadingIndicator: provided => ({
    ...provided,
    padding: 0,
    color: 'var(--dds-color-icon-default)',
  }),
});
